/**
 * Tiny DOM builders. Screens build their tree once at construction; the per-frame
 * path only writes `textContent` / `style` properties on cached references
 * (INTERFACES.md ground rule 2 — nothing allocates per frame).
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function div(className?: string, text?: string): HTMLDivElement {
  return el('div', className, text);
}

export function span(className?: string, text?: string): HTMLSpanElement {
  return el('span', className, text);
}

/**
 * AMD-13 — a coloured bracketed delta, dwarf.land's `(+2 food)` / `(+2 iron)`.
 *
 * The bracket and the colour do the work together: the parens mark "this is a change,
 * not a state", and the tone carries §11.2.6's meaning unchanged — `xp` green, `hp`
 * red-pink, `surge` player gold, `hazard` magenta, `dim` for a neutral state change.
 * Call sites pass the body without brackets; the brackets are the component's.
 */
export type DeltaTone = 'xp' | 'hp' | 'surge' | 'hazard' | 'dim';

export function delta(body: string, tone: DeltaTone = 'dim'): HTMLSpanElement {
  return span('delta is-' + tone, '(' + body + ')');
}

export function setDelta(node: HTMLElement, body: string, tone: DeltaTone = 'dim'): void {
  setText(node, body ? '(' + body + ')' : '');
  const next = 'delta is-' + tone;
  if (node.className !== next) node.className = next;
}

export const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Bare SVG element builder for the handful of places that *draw* rather than
 * reference a sprite symbol — currently the §13.1.2 arena silhouettes, whose
 * geometry comes straight from the §10.8 variant table.
 */
export function svgTag<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Readonly<Record<string, string>>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  const keys = Object.keys(attrs);
  for (let i = 0; i < keys.length; i++) node.setAttribute(keys[i], attrs[keys[i]]);
  return node;
}

/** Pixel icon from the inlined hackernoon sprite in `index.html` (MIT). */
export function icon(name: string, className?: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'ic' + (className ? ' ' + className : ''));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const use = document.createElementNS(SVG_NS, 'use');
  use.setAttribute('href', '#i-' + name);
  svg.appendChild(use);
  return svg;
}

/**
 * Every interactive control goes through here: ≥ 44×44 CSS px is enforced in
 * `ui.css` via `.btn { min-block-size: 44px; min-inline-size: 44px }` (§13.5 Fitts' floor).
 */
export function button(label: string, className?: string, iconName?: string): HTMLButtonElement {
  const b = el('button', 'btn' + (className ? ' ' + className : ''));
  b.type = 'button';
  if (iconName) b.appendChild(icon(iconName));
  const t = span('btn-label', label);
  b.appendChild(t);
  return b;
}

export function setLabel(b: HTMLButtonElement, label: string): void {
  const t = b.querySelector('.btn-label');
  if (t) t.textContent = label;
}

/** Panel with the canvasui-style corner ticks (§11.2.6 `pal.ui.panel`). */
export function panel(className?: string): HTMLDivElement {
  const p = div('panel' + (className ? ' ' + className : ''));
  p.appendChild(div('panel-corner tl'));
  p.appendChild(div('panel-corner tr'));
  p.appendChild(div('panel-corner bl'));
  p.appendChild(div('panel-corner br'));
  const body = div('panel-body');
  p.appendChild(body);
  return p;
}

export function panelBody(p: HTMLElement): HTMLElement {
  return p.querySelector('.panel-body') as HTMLElement;
}

/** Horizontal meter with a fill child; `setMeter` is the per-frame writer. */
export function meter(className?: string): HTMLDivElement {
  const m = div('meter' + (className ? ' ' + className : ''));
  m.appendChild(div('meter-fill'));
  return m;
}

export function meterFill(m: HTMLElement): HTMLElement {
  return m.firstElementChild as HTMLElement;
}

/** Writes only when the rounded percent changes — avoids style thrash per frame. */
export function setMeter(fill: HTMLElement, frac01: number): void {
  const pct = Math.round(Math.max(0, Math.min(1, frac01)) * 1000) / 10;
  const next = pct + '%';
  if (fill.style.width !== next) fill.style.width = next;
}

export function setText(node: Node, value: string): void {
  if (node.textContent !== value) node.textContent = value;
}

export function setHidden(node: HTMLElement, hidden: boolean): void {
  if (node.hidden !== hidden) node.hidden = hidden;
}

export function setClass(node: Element, name: string, on: boolean): void {
  if (node.classList.contains(name) !== on) node.classList.toggle(name, on);
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Labelled row used by every settings control. */
export function field(label: string, control: HTMLElement, hint?: string): HTMLDivElement {
  const row = div('field');
  const l = div('field-label', label);
  row.appendChild(l);
  row.appendChild(control);
  if (hint) row.appendChild(div('field-hint', hint));
  return row;
}

/** Segmented option control — a radio group that gamepad nav treats as one stop. */
export function segmented(
  options: readonly { id: string; label: string }[],
  selected: string,
  onPick: (id: string) => void,
): HTMLDivElement {
  const wrap = div('segmented');
  wrap.setAttribute('role', 'radiogroup');
  for (let i = 0; i < options.length; i++) {
    const o = options[i];
    const b = button(o.label, 'seg');
    b.dataset.value = o.id;
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', String(o.id === selected));
    b.classList.toggle('is-on', o.id === selected);
    b.addEventListener('click', () => {
      const kids = wrap.children;
      for (let k = 0; k < kids.length; k++) {
        const c = kids[k] as HTMLElement;
        const on = c === b;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-checked', String(on));
      }
      onPick(o.id);
    });
    wrap.appendChild(b);
  }
  return wrap;
}

export function setSegmented(wrap: HTMLElement, selected: string): void {
  const kids = wrap.children;
  for (let k = 0; k < kids.length; k++) {
    const c = kids[k] as HTMLElement;
    const on = c.dataset.value === selected;
    c.classList.toggle('is-on', on);
    c.setAttribute('aria-checked', String(on));
  }
}

export function toggle(label: string, on: boolean, onChange: (v: boolean) => void): HTMLDivElement {
  const control = segmented(
    [
      { id: 'off', label: 'Off' },
      { id: 'on', label: 'On' },
    ],
    on ? 'on' : 'off',
    (id) => onChange(id === 'on'),
  );
  control.classList.add('toggle');
  return field(label, control);
}

export interface SliderRefs {
  root: HTMLDivElement;
  input: HTMLInputElement;
  readout: HTMLSpanElement;
}

export function slider(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  format: (v: number) => string,
  onChange: (v: number) => void,
): SliderRefs {
  const wrap = div('slider');
  const input = el('input', 'slider-input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  const readout = span('slider-readout', format(value));
  /*
   * `--pct` drives the track's fill, because `accent-color` cannot be told what to do with the
   * half it does not paint.
   *
   * A native range fills from `accent-color` and derives the REMAINDER itself, by contrast
   * against that colour. With gold, Chrome picked a dark remainder and the bar read correctly;
   * with a mid-luminance chrome value it picked white, and the bar read inverted — a pale
   * "empty" half brighter than the filled one. Both halves have to be declared.
   */
  const paint = (v: number): void => {
    const pct = max > min ? ((v - min) / (max - min)) * 100 : 0;
    input.style.setProperty('--pct', pct.toFixed(2) + '%');
  };
  paint(value);
  input.addEventListener('input', () => {
    const v = Number(input.value);
    readout.textContent = format(v);
    paint(v);
    onChange(v);
  });
  wrap.appendChild(input);
  wrap.appendChild(readout);
  const root = field(label, wrap);
  return { root, input, readout };
}

/**
 * §13.1.5 / §13.1.11 hold-to-confirm. The button reads "Hold to confirm" and fires
 * after 500 ms of continuous press; releasing early cancels with no side effect.
 */
export function holdConfirm(b: HTMLButtonElement, label: string, confirmLabel: string, onConfirm: () => void): void {
  let timer = 0;
  let armed = false;
  const start = (ev: Event): void => {
    ev.preventDefault();
    if (armed) return;
    armed = true;
    setLabel(b, confirmLabel);
    b.classList.add('is-holding');
    timer = window.setTimeout(() => {
      cancel();
      onConfirm();
    }, 500);
  };
  const cancel = (): void => {
    if (!armed) return;
    armed = false;
    window.clearTimeout(timer);
    setLabel(b, label);
    b.classList.remove('is-holding');
  };
  b.addEventListener('pointerdown', start);
  b.addEventListener('pointerup', cancel);
  b.addEventListener('pointercancel', cancel);
  b.addEventListener('pointerleave', cancel);
  b.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') start(e);
  });
  b.addEventListener('keyup', cancel);
  b.addEventListener('blur', cancel);
}
